import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, Sparkles, CheckCircle } from 'lucide-react';
import { mockSegments } from '../../shared/data/segments';

export const SolutionsHub: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const segmentsList = Object.values(mockSegments);

  const filteredSegments = segmentsList.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    s.tagline.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#050505] text-slate-100 font-sans selection:bg-teal-500 selection:text-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#050505]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-500 flex items-center justify-center text-slate-950 font-black text-xl">
              Y
            </div>
            <div>
              <span className="font-extrabold text-xl tracking-tight text-white block leading-none">YouMachine</span>
              <span className="text-[10px] text-teal-400 uppercase tracking-widest">Biblioteca de Soluções 360º</span>
            </div>
          </div>
          <Link 
            to="/admin" 
            className="px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold text-xs uppercase tracking-wider transition-colors"
          >
            Painel Admin
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 text-teal-400 border border-teal-500/20 mb-6 text-xs font-bold uppercase tracking-widest">
            <Sparkles className="w-4 h-4" />
            Estratégia Digital Canônica por Nicho
          </div>
          <h1 className="text-4xl sm:text-6xl font-black text-white tracking-tight leading-tight mb-6">
            Descubra como sua empresa pode ter uma <br />
            <span className="text-teal-400">estrutura digital 360º de classe mundial.</span>
          </h1>
          <p className="text-lg text-slate-300 font-light max-w-2xl mx-auto leading-relaxed mb-10">
            Selecione o seu segmento para explorar modelos visuais de Site, Aplicativo, Biolink, Sistema de Gestão e Estratégia de Vendas desenhados sob medida para o seu mercado.
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto bg-[#111111] p-2 rounded-2xl border border-white/10 shadow-2xl flex items-center gap-3">
            <Search className="w-5 h-5 text-slate-400 ml-3" />
            <input 
              type="text" 
              placeholder="Digite seu segmento (ex: Imobiliária, Restaurante, Contabilidade...)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-transparent text-sm text-white placeholder-slate-500 outline-none"
            />
          </div>
        </div>
      </section>

      {/* Segments Catalog Grid */}
      <section className="py-12 px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          {filteredSegments.map((segment) => (
            <div 
              key={segment.slug} 
              className="group bg-[#111111] rounded-3xl p-8 border border-white/10 hover:border-teal-500/50 transition-all duration-300 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div 
                    className="w-12 h-12 rounded-2xl flex items-center justify-center text-white"
                    style={{ backgroundColor: `${segment.primary_color}30`, borderColor: `${segment.primary_color}60`, borderWidth: 1 }}
                  >
                    <Sparkles className="w-6 h-6" style={{ color: segment.accent_color }} />
                  </div>
                  <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/5 border border-white/10 text-slate-300">
                    Blueprint 360º
                  </span>
                </div>

                <h3 className="text-2xl font-extrabold text-white mb-2 group-hover:text-teal-400 transition-colors">
                  {segment.name}
                </h3>
                <p className="text-slate-400 text-sm font-light leading-relaxed mb-6">
                  {segment.tagline}
                </p>

                <div className="space-y-2 mb-8">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">O que inclui neste segmento:</div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-300">
                    <div className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-teal-400" /> Site & SEO</div>
                    <div className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-teal-400" /> App PWA / Mobile</div>
                    <div className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-teal-400" /> Sistema & CRM</div>
                    <div className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-teal-400" /> Tráfego & Google</div>
                  </div>
                </div>
              </div>

              <Link 
                to={`/solucoes/${segment.slug}`}
                className="w-full py-4 rounded-xl bg-white/10 hover:bg-teal-500 hover:text-black font-bold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 group-hover:shadow-lg"
              >
                Explorar Solução 360º <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
